import {DataModificationsHeader} from 'audit/DataModifications';
import {Permissions} from 'auth/PermissionConstants';
import {BioentityAliasInfo, LabelingStrategy} from 'constants/RegistryConstants';
import {BioentitySummary, Tag} from 'folder-items/FileConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ValidationErrorData} from './RegistryValidatorStore';

interface CheckItemsResponse {
  myCollectionErrors: {[key: string]: ValidationErrorData};
  orgCollectionErrors: {[key: string]: ValidationErrorData};
  files: Array<BioentitySummary>;
  addedFileIds: Array<string>;
}

/*
 * Validates the given files for a move into a registry.
 * @param  {String[]} fileIds
 * @param  {String} registryId  destination registry for the files
 * @return {Deferred}
 */
export function checkItems(fileIds: Array<string>, registryId: string): JQueryPromise<CheckItemsResponse> {
  return TaskRunner.runTask({
    url: `/1/api/registries/${registryId}/actions/validate-moving-files`,
    type: 'POST',
    contentType: 'application/json',
    dataType: 'json',
    data: JSON.stringify({fileIds}),
  });
}

/*
 * Validates the given file's tags for a move into a registry.
 * @param  {String} fileId
 * @param  {String} registryId  destination registry for the files
 */
export function validateFileTags(
  fileId: string,
  registryId: string
): // Return type corresponds to RegistryValidationError.to_dict()
JQueryPromise<{
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  field: any;
  errorLabel: string;
  message: string;
}> {
  return net.postJSON({
    url: `/1/api/registries/${registryId}/actions/validate-file-tags`,
    data: {fileId},
  });
}

// Corresponds to output of RegistrationInfo.to_dict() + permissionsById
export interface SubmitMoveResponse {
  bioentityAliasInfosById: {[id: string]: Array<BioentityAliasInfo>};
  changedNamesById: {[id: string]: string};
  changedDisplayLabelsById: {[id: string]: string};
  archivedFileIds: Array<string>;
  tagsById: {[id: string]: Array<Tag>};
  permissionsById: {[id: string]: Permissions};
  numMerged: number;
  numMoved: number;
}

export interface RegistryOptions {
  inUserCollection: boolean;
  inOrgCollection: boolean;
  labelingStrategy: LabelingStrategy;
}

/*
 * Move the given fileIds in to the registry
 * @param  {String[]} fileIds
 * @param  {String} registryId  destination registry for the files
 * @param  {String} tagSchemaId  schema to add to each file
 * @param  {Object} warningCodes  mapping from fileId to warningCode that the user has acknowledged
 * @return {Deferred}
 */
export function submitMove(
  fileIds: Array<string>,
  registryId: string,
  warningCodes: {[id: string]: string},
  {inUserCollection, inOrgCollection, labelingStrategy}: RegistryOptions,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<SubmitMoveResponse> {
  // Temporarily, inUserCollection/inOrgCollection can be undefined, until we remove the old folder view
  return TaskRunner.postJSON({
    url: `/1/api/registries/${registryId}/actions/move-files`,
    data: {
      fileIds,
      warningCodes,
      inUserCollection,
      inOrgCollection,
      labelingStrategy,
    },
    extraHeaders: dataModsHeader,
  });
}
